/**
 * Turning a failed `translateTurn`/`speak`/`updateLearner` into copy the
 * learner can act on.
 *
 * relay-runtime (21.0.1, `OperationExecutor.js`'s `_handleErrorResponse`)
 * routes every server refusal through `onError` here: all three mutations
 * declare non-null payloads, so a GraphQL error nulls `data` and the whole
 * response becomes a thrown `RelayError` rather than a value carried on the
 * result. That error carries the original GraphQL errors verbatim at
 * `error.source.errors`, each with the `extensions.code` the server set
 * (`crates/tutor-server/src/graphql/errors.rs`'s `coded()`, and
 * `limits.rs`'s separate `"RATE_LIMITED"`). `codeFromError` reads that path;
 * `copyFor` is the map from code to what the learner sees. `AccountProfile`
 * reads the same two functions rather than a second copy of this reasoning --
 * a final-review finding caught its own `onCompleted`-branch handling as dead
 * code, because it assumed refusals arrived beside a response instead.
 *
 * Server messages never reach the screen. They are written for someone
 * reading a GraphQL response, not a learner mid-conversation, and passing
 * one through would put arbitrary server prose in the client -- every case
 * below is copy this component owns.
 */

import { msg, type Message } from '../i18n/t.ts';

/** A GraphQL error shaped the way relay-runtime's thrown `RelayError` is. */
type RelayThrownError = {
  source?: {
    errors?: ReadonlyArray<{ extensions?: Record<string, unknown> } | null | undefined> | null;
  };
};

/**
 * The refusal's `extensions.code`, or null for anything that isn't one --
 * a plain network failure, a shape this client doesn't recognise, or no
 * error at all.
 */
export function codeFromError(error: unknown): string | null {
  const source = (error as RelayThrownError | null | undefined)?.source;
  const code = source?.errors?.[0]?.extensions?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * What the learner sees for each refusal `translateTurn`/`speak`/
 * `updateLearner` can send.
 *
 * `BAD_USER_INPUT` now covers four server-side cases -- no native language
 * set at all, a stored language the recogniser doesn't handle, one that
 * resolves to English (ruling Y), and (also ruling Y, on `updateLearner`) a
 * freshly typed one that resolves to English. `validateProfile`'s
 * `looksEnglish` mirrors the ordinary spellings of the last case client-side,
 * so this message is the backstop for a spelling that mirror misses rather
 * than the primary defence. The other three reach only `translateTurn`/
 * `speak`: `aidsFor` withholds `'translate'` whenever `hasNativeLanguage` is
 * false, but does not check `translationTarget` (`AidsPanel.tsx`'s own doc
 * comment on `translateInto` has the reasoning), so a pre-ruling row whose
 * native language resolves to English can still reach the third case there.
 * All four share one message below -- each is some shape of "the stored
 * language doesn't work as a target," and the learner cannot tell them apart
 * from here anyway.
 *
 * Everything else -- `INTERNAL_SERVER_ERROR` (oMLX unreachable), `CONFLICT`,
 * a code this client has never seen, or no code at all (a genuine network
 * failure) -- shares one fallback. It deliberately does not name the network,
 * because for the two most common refusals (a spent rate limit, an unusable
 * language) that would be a diagnosis the client cannot actually make.
 */
export function copyFor(code: string | null): Message {
  switch (code) {
    case 'RATE_LIMITED':
      return msg('aid-rate-limited');
    case 'NOT_FOUND':
      return msg('aid-not-found');
    case 'BAD_USER_INPUT':
      return msg('aid-bad-language');
    case 'UNAUTHENTICATED':
      return msg('aid-unauthenticated');
    default:
      return msg('aid-failed');
  }
}
